/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EnumValues } from '@card-software/library';
import { TreeNode } from 'primeng/api';
import { TreeTableLazyLoadEvent } from 'primeng/treetable';
import { Fabric } from '../../../../models/fabric/fabric';
import { AVAILABLE_ROLLS_CONFIG, DispatchRollsTreeTableConfig } from './dispatch-rolls-tree-table.models';

@Component({
  selector: 'app-dispatch-rolls-tree-table',
  templateUrl: './dispatch-rolls-tree-table.component.html',
  styleUrls: ['./dispatch-rolls-tree-table.component.scss'],
})
export class DispatchRollsTreeTableComponent {
  @Input() treeData: TreeNode[] = [];
  @Input() config: DispatchRollsTreeTableConfig = AVAILABLE_ROLLS_CONFIG;

  @Input() selectedNodes: TreeNode[] = [];
  @Output() selectedNodesChange = new EventEmitter<TreeNode[]>();

  /**
   * Server-side pagination (opt-in, off by default so the non-paginated tables keep rendering the
   * whole tree). With `lazy` on, the TreeTable never slices `treeData` itself: the parent is
   * responsible for loading the page and for keeping `first`/`totalRecords` in sync.
   */
  @Input() lazy = false;
  @Input() paginator = false;
  @Input() rows = 100;
  @Input() first = 0;
  @Input() totalRecords = 0;
  @Input() rowsPerPageOptions: number[] = [25, 50, 100];
  @Output() lazyLoad = new EventEmitter<TreeTableLazyLoadEvent>();

  @Input() getColorName: (colorId: string) => string = (id) => id;
  @Input() lbsToKg: (lbs: number) => number = (lbs) => lbs * 0.453592;
  @Input() getFabricByCode: (code: string) => Fabric | null = () => null;
  @Input() fabricFormValues: EnumValues = {};

  @Output() rollDeleted = new EventEmitter<{ rollNumber: string }>();

  onSelectionChange(selection: TreeNode[] | TreeNode | null): void {
    const nodes = Array.isArray(selection) ? selection : selection ? [selection] : [];
    this.selectedNodes = nodes;
    this.selectedNodesChange.emit(nodes);
  }

  onDeleteRoll(rollNumber: string): void {
    this.rollDeleted.emit({ rollNumber });
  }

  getRollQuality(rowData: any): number | undefined {
    return rowData.rollQuality ?? rowData.scannedRoll?.roll?.rollQuality;
  }

  getWeightLbs(rowData: any): number {
    return rowData.finalWeight ?? rowData.weightInLbs ?? 0;
  }

  getWeightKg(rowData: any): number {
    if (rowData.weightInKg != null) {
      return rowData.weightInKg;
    }
    return this.lbsToKg(this.getWeightLbs(rowData));
  }

  getPcLabel(rowData: any): string {
    return rowData.label || rowData.productionCardNumber || '';
  }

  getColumnCount(): number {
    let count = 1;
    if (this.config.showColor) count++;
    if (this.config.showQuality) count++;
    if (this.config.showFabric) count++;
    if (this.config.showType) count++;
    if (this.config.showWeightLbs) count++;
    if (this.config.showWeightKg) count++;
    if (this.config.showWeightYds) count++;
    if (this.config.showScannedAt) count++;
    if (this.config.showScannedBy) count++;
    if (this.config.showActions) count++;
    return count;
  }

  getTableMinWidth(): string {
    let rem = 22;
    if (this.config.showColor) rem += 10;
    if (this.config.showQuality) rem += 6;
    if (this.config.showFabric) rem += 16;
    if (this.config.showType) rem += 10;
    if (this.config.showWeightLbs) rem += 9;
    if (this.config.showWeightKg) rem += 9;
    if (this.config.showWeightYds) rem += 9;
    if (this.config.showScannedAt) rem += 12;
    if (this.config.showScannedBy) rem += 10;
    if (this.config.showActions) rem += 7;
    return `${rem}rem`;
  }
}
